import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  Ref,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IonButton, IonIcon, IonItem, IonLabel, IonList } from '@ionic/react';
import 'swiper/css';
import './FarecardListItem.css';
import { Farecard, FarecardCappingPot } from '../../services/types/apitypes';
import VisualFarecard from './VisualFarecard';
import { apiGetCappingPots } from '../../services/api';
import useEnhancedAuth from '../../EnhancedAuth';
import FarecardCappingCard from './FarecardCappingCard';
import {
  storageGetCappingPots,
  storageSetCappingPots,
} from '../../utils/storage';
import FarecardCappingCardComplete from './FarecardCappingCardComplete';
import { useHistory } from 'react-router';
import NFCValidator from './NFCValidator';
import {formatCreditCard} from "../../utils/strings";

const CAPPING_ID_FOR_METRO = 9; // This is the potId for fare capping in Metro

const defaultPot: FarecardCappingPot = {
  potId: CAPPING_ID_FOR_METRO,
  amount: 10,
  currentValue: 0,
  name: '1-For-10 Loyalty Program',
  validFrom: '',
  validTo: '',
  missingValue: 10,
  description: '',
  dateCapped: '',
};

type FarecardListItemProps = {
  item: Farecard;
  goToAdd: () => void;
  addValue: (f: Farecard) => void;
  unlock: (f: Farecard) => void;
  showOptions: (f: Farecard) => void;
  ref: Ref<{ fetchCappingPots: () => void }>;
};

const FarecardListItem = forwardRef<
  { fetchCappingPots: () => void },
  FarecardListItemProps
>(({ item, goToAdd, unlock, addValue, showOptions }, ref) => {
  const [t] = useTranslation('global');
  const auth = useEnhancedAuth();
  const router = useHistory();

  const [pots, setPots] = useState<FarecardCappingPot[] | undefined>();

  const pot = useMemo<FarecardCappingPot | undefined>(() => {
    //We'll be using specifically potId 9 for the fare capping pot
    return (
      pots?.find((pot) => pot.potId === CAPPING_ID_FOR_METRO) || defaultPot
    );
    if (pots) {
      let amount = 0;
      let percentage = 0;

      let pot;

      const setCurrPot = (currPot: FarecardCappingPot): void => {
        amount = currPot.amount;
        percentage = currPot.currentValue / currPot.amount;

        pot = currPot;
      };

      for (const currPot of pots!) {
        if (
          currPot.currentValue / currPot.amount === percentage &&
          currPot.amount > amount
        ) {
          setCurrPot(currPot);
        }
        if (currPot.currentValue / currPot.amount > percentage) {
          setCurrPot(currPot);
        }
      }
      return pot;
    }
  }, [pots]);

  const potElement = useMemo<React.ReactElement | undefined>(() => {
    if (!pot) return;

    const isPotComplete = pot.missingValue === 0;

    const PotCard = isPotComplete
      ? FarecardCappingCardComplete
      : FarecardCappingCard;
    const potTitle = !isPotComplete && (
      <h4 className="fare-capping-title">
        {t('farecapping.fareCappingNextFreePass')}
      </h4>
    );

    return (
      <>
        {potTitle}
        <PotCard
          pot={pot}
          /** We'll only be using one pot for now, so we don't need to go to fare capping screen
          onClick={() => router.push('/card/' + item.cardId + '/fares')} */
        />
      </>
    );
  }, [pot, item.cardId, router, t]);

  const cardName = useMemo(() => {
    if (item.nickname) {
      return item.nickname;
    }
    let cardId = item.cardId;
    if (cardId) {
      // remove leading 1 and zeros
      cardId = cardId.replace(/^10+/, '').slice(-4);
    }
    return t('farecards.cardprefix') + ' ' + formatCreditCard(cardId);
  }, [t, item.nickname, item.cardId]);

  const fetchCappingPots = useCallback(() => {
    if (item.cardId) {
      const stored = storageGetCappingPots(item.cardId);
      if (stored) setPots(stored);
      apiGetCappingPots(item.cardId, auth)
        .then((response) => {
          storageSetCappingPots(response.data, item.cardId!);
          setPots(response.data);
        })
        .catch((e) => console.log('Could not get capping pots: ', e));
    }
  }, [item.cardId, auth]);

  useImperativeHandle(
    ref,
    () => ({
      fetchCappingPots: fetchCappingPots,
    }),
    [fetchCappingPots],
  );

  useEffect(() => {
    fetchCappingPots();
  }, [fetchCappingPots]);

  return (
    <>
      <div className="farecards-header pb-2">
        <h1 className="h1 no-translate">{cardName}</h1>
        <IonIcon onClick={goToAdd} src={'assets/icon/card-inv2.svg'}></IonIcon>
      </div>

      <VisualFarecard item={item}></VisualFarecard>

      <NFCValidator />

      {potElement}

      {item.locked ? (
        <div className="pt-2 pb-5 ion-text-center">
          <IonButton
            disabled={!item.unblockPossible}
            className="btn-default "
            expand="block"
            onClick={() => unlock(item)}
          >
            {t('farecards.unlock')}
          </IonButton>
          {!item.unblockPossible && <IonLabel color={"danger"}>{t('farecards.unlockdisabled')}</IonLabel>}
        </div>
      ) : (
        <IonList className="pt-2 pb-5">
          <IonItem
            onClick={() => addValue(item)}
            lines="none"
            className="ion-no-padding ion-pt-1 pseudo-select pseudo-select-option"
            button={true}
            detail={true}
          >
            <IonIcon
              slot="start"
              src="assets/icon/card-add-value.svg"
            ></IonIcon>
            <IonLabel>{t('farecards.addvalue')}</IonLabel>
          </IonItem>
          <IonItem
            onClick={() => showOptions(item)}
            lines="none"
            className="ion-no-padding ion-pt-1 pseudo-select pseudo-select-option"
            button={true}
            detail={true}
          >
            <IonIcon slot="start" src="assets/icon/card-options.svg"></IonIcon>
            <IonLabel>{t('farecards.cardoptions')}</IonLabel>
          </IonItem>
        </IonList>
      )}
    </>
  );
});

FarecardListItem.displayName = 'FarecardListItem'; // Set display name for debugging

export default FarecardListItem;
